import React, { useEffect, useState } from 'react';
import { Box, ButtonBase, Chip, Stack, SxProps, Tooltip } from '@mui/material';
import { useLocation } from 'react-router-dom';

import moment from 'moment';
import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';
import { ISection, LESSON_TYPES } from '../../../../store/V2/SectionSlice';
import { getCoursesState } from '../../../../store/V2/CoursesSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import { getClassShortUrlByWaitingId } from '../../../../helper';
import ZoomSectionItem, { getZoomRequestStatusColor } from './ZoomSectionItem';
import ZoomMeetingOptionsButtonWithMenu from './ZoomMeetingOptionsButtonWithMenu';
import {
  MEETING_REQUEST_STATUSES,
  MEETING_REQUEST_TYPES,
} from '../../../../store/V2/MeetingRequestsSlice';
import ViewHomeworkModal from '../modals/ViewHomeworkModal';
import TeamsSectionItem from './TeamsSectionItem';

interface MixedSectionContentProps {
  zoomData?: ISection;
  teamsData?: ISection;
  resourceData?: ISection;
  homeworkData?: ISection;
  isCourseSubscribed?: boolean;
  isOneToOne: boolean;
  onCancelClass: (section: ISection) => void;
  onRescheduleClass: (section: ISection) => void;
}

const buttonBaseStyles: SxProps = {
  p: '8px 12px 8px 12px',
  borderRadius: '5px',
  fontSize: '12px',
  fontWeight: 600,
};

const animationStyles: SxProps = {
  position: 'absolute',
  right: '-10px',
  top: '25%',
  transform: 'translateY(-50%)',
  width: '12px',
  height: '12px',
  border: '2px solid black',
  borderRadius: '50%',
  zIndex: 1,
  animation: 'wave-animation 1.5s infinite',
  '@keyframes wave-animation': {
    '0%': {
      transform: 'scale(1)',
      opacity: 0.8,
    },
    '25%': {
      transform: 'scale(1.2)',
      opacity: 0.7,
    },
    '50%': {
      transform: 'scale(1.3)',
      opacity: 0.5,
    },
    '75%': {
      transform: 'scale(1.4)',
      opacity: 0.4,
    },
    '100%': {
      transform: 'scale(1.5)',
      opacity: 0.3,
    },
  },
};

const MixedSectionContent: React.FC<MixedSectionContentProps> = ({
  zoomData,
  teamsData,
  resourceData,
  homeworkData,
  isCourseSubscribed,
  isOneToOne,
  onCancelClass,
  onRescheduleClass,
}) => {
  // Store
  const { userCourse } = useAppSelector(getCoursesState);
  const { user } = useAppSelector(getAuthState);

  // State
  const [isViewHomeworkModalOpen, setViewHomeworkModalOpen] = useState(false);

  const meetingData = zoomData || teamsData;
  const isCourseTutor = userCourse?.tutors?.some(
    (tutor) => tutor?.tutor?._id === user?._id
  );

  const location = useLocation();
  const [isTooltipOpen, setTooltipOpen] = useState(true);
  const searchParams = new URLSearchParams(location.search);
  const scrollId = searchParams.get('scroll');
  const noAnim = searchParams.get('noanim') === 'true';
  const shouldShowTooltip = !noAnim && scrollId === meetingData?._id;
  let classLink = '';

  const isZoomRequestCanceled =
    meetingData &&
    meetingData.zoomRequest &&
    meetingData.zoomRequest.type === MEETING_REQUEST_TYPES.CANCEL &&
    meetingData.zoomRequest.status === MEETING_REQUEST_STATUSES.APPROVED;

  const isZoomRequestNotPending = !(
    meetingData &&
    meetingData.zoomRequest &&
    meetingData.zoomRequest.status === MEETING_REQUEST_STATUSES.PENDING
  );

  const isClassPassed =
    meetingData &&
    meetingData.dateTime &&
    meetingData.duration &&
    moment(meetingData.dateTime).add(meetingData.duration, 'minutes') <
      moment();

  if (
    isCourseSubscribed &&
    meetingData &&
    meetingData.classWaitingId &&
    !isZoomRequestCanceled &&
    isZoomRequestNotPending &&
    !isClassPassed
  ) {
    classLink = getClassShortUrlByWaitingId(meetingData.classWaitingId);
  }

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setTooltipOpen(false);
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <Stack gap={2} style={{ marginBottom: '15px' }}>
      {meetingData ? (
        <Stack gap={2}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Chip
              size="small"
              label={meetingData.type.toLowerCase()}
              color="info"
              sx={{ width: 'fit-content', textTransform: 'capitalize' }}
            />
            {meetingData.zoomRequest && isCourseSubscribed ? (
              <Chip
                size="small"
                label={`${meetingData.zoomRequest.type} - ${meetingData.zoomRequest.status}`}
                color={getZoomRequestStatusColor(
                  meetingData.zoomRequest.status
                )}
                sx={{ width: 'fit-content' }}
              />
            ) : null}
          </Stack>
          {zoomData ? (
            <ZoomSectionItem
              section={zoomData}
              innerContentOnly
              onCancelClass={onCancelClass}
              isOneToOne={isOneToOne}
              onRescheduleClass={onRescheduleClass}
            />
          ) : null}
          {teamsData ? (
            <TeamsSectionItem
              section={teamsData}
              innerContentOnly
              onCancelClass={onCancelClass}
              isOneToOne={isOneToOne}
              onRescheduleClass={onRescheduleClass}
            />
          ) : null}
        </Stack>
      ) : null}
      <Stack direction="row" alignItems="center" gap={1}>
        {classLink &&
        meetingData &&
        meetingData.lessonType !== LESSON_TYPES.DUMMY ? (
          <Tooltip
            title={shouldShowTooltip ? 'Join Now' : ''}
            placement="right"
            arrow
            open={isTooltipOpen}
            disableInteractive
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 9],
                  },
                },
              ],
              sx: {
                '.MuiTooltip-tooltip': {
                  backgroundColor: '#1E293B',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  '.MuiTooltip-arrow': {
                    color: '#1E293B',
                  },
                },
              },
            }}
          >
            <ButtonBase
              id={`${meetingData._id}`}
              href={classLink}
              target="_blank"
              sx={{
                ...buttonBaseStyles,
                backgroundColor: '#3498DB',
                color: TP_COLORS.white,
              }}
              onMouseEnter={handleTooltipOpen}
            >
              JOIN NOW
              {shouldShowTooltip && <Box sx={animationStyles} />}
            </ButtonBase>
          </Tooltip>
        ) : null}
        {isCourseSubscribed &&
        meetingData &&
        meetingData.lessonType !== LESSON_TYPES.TRIAL &&
        meetingData.lessonType !== LESSON_TYPES.DUMMY ? (
          <ZoomMeetingOptionsButtonWithMenu
            section={meetingData}
            onCancelClass={onCancelClass}
            isOneToOne={isOneToOne}
            onRescheduleClass={onRescheduleClass}
          />
        ) : null}
        {resourceData && resourceData.quiztopiyaResourceUrl ? (
          <ButtonBase
            href={`${process.env.PUBLIC_URL}/${resourceData.quiztopiyaResourceUrl}`}
            target="_blank"
            sx={{
              ...buttonBaseStyles,
              backgroundColor: '#1E293B',
              color: TP_COLORS.white,
            }}
          >
            View Resource
          </ButtonBase>
        ) : null}
        {homeworkData && (isCourseTutor || isCourseSubscribed) ? (
          <>
            <ButtonBase
              onClick={() => setViewHomeworkModalOpen(true)}
              sx={{
                ...buttonBaseStyles,
                backgroundColor: TP_COLORS.border.color1,
                color: TP_COLORS.gray.darken,
              }}
            >
              Homework
            </ButtonBase>
            {isViewHomeworkModalOpen && (
              <ViewHomeworkModal
                open={isViewHomeworkModalOpen}
                onClose={() => setViewHomeworkModalOpen(false)}
                section={homeworkData}
              />
            )}
          </>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default MixedSectionContent;
